import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBusinessWorkflowStats } from "../../businessModeService";

const STEP_DURATIONS_MS = [700, 1400, 1700, 1700, 1400];
const FAST_STEP_DURATION_MS = 280;
const FINISH_DELAY_MS = 650;
const CALL_COUNT_POLL_MS = 4000;

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.4">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Full-screen first-pull progress shown after onboarding "See my action items".
 *
 * @example
 * // Input
 * <ActionItemsFirstPullScreen
 *   lookbackDays={15}
 *   workflowId="wf-1"
 *   readyToFinish={items.length > 0}
 *   onFinished={() => navigate("/business/action-items", { replace: true })}
 * />
 * // Output: centered checklist that advances step-by-step, then calls onFinished
 */
const ActionItemsFirstPullScreen = ({
  lookbackDays,
  workflowId = "",
  readyToFinish = false,
  onFinished,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [callCount, setCallCount] = useState(null);
  const [fastForward, setFastForward] = useState(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    const id = String(workflowId || "").trim();
    if (!id) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const stats = await getBusinessWorkflowStats(id);
        const extracted = Number(stats?.last_connector_job?.extracted_count);
        if (!cancelled && Number.isFinite(extracted) && extracted > 0) {
          setCallCount(extracted);
        }
      } catch {
        // Stats may not exist until the first connector job finishes.
      }
    };

    poll();
    const intervalId = setInterval(poll, CALL_COUNT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [workflowId]);

  const steps = useMemo(() => {
    const scanLabel = `Scanning your last ${lookbackDays} days of calls`;
    const readLabel =
      callCount != null
        ? `Found ${callCount} calls — reading every transcript`
        : "Reading every transcript";
    return [
      "Connecting to your sources",
      scanLabel,
      readLabel,
      "Catching the promises you made",
      "Lining up your follow-ups",
    ];
  }, [callCount, lookbackDays]);

  useEffect(() => {
    if (readyToFinish) {
      setFastForward(true);
    }
  }, [readyToFinish]);

  useEffect(() => {
    if (activeStepIndex >= steps.length) return undefined;
    const duration = fastForward ? FAST_STEP_DURATION_MS : STEP_DURATIONS_MS[activeStepIndex];
    const id = setTimeout(() => {
      setActiveStepIndex((index) => index + 1);
    }, duration);
    return () => clearTimeout(id);
  }, [activeStepIndex, fastForward, steps.length]);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinished?.();
  }, [onFinished]);

  useEffect(() => {
    if (activeStepIndex < steps.length) return undefined;
    const id = setTimeout(finish, FINISH_DELAY_MS);
    return () => clearTimeout(id);
  }, [activeStepIndex, finish, steps.length]);

  return (
    <div className="action-items-first-pull" role="status" aria-live="polite">
      <div className="action-items-first-pull__title">Pulling your action items…</div>
      <p className="action-items-first-pull__subtitle">
        This can take a few minutes. Sit tight — we&apos;ll drop you on your list the moment
        it&apos;s ready.
      </p>
      <ul className="action-items-first-pull__steps">
        {steps.map((label, index) => {
          const isDone = index < activeStepIndex;
          const isActive = index === activeStepIndex;
          const stateClass = isDone
            ? "action-items-first-pull__step--done"
            : isActive
              ? "action-items-first-pull__step--active"
              : "action-items-first-pull__step--pending";
          return (
            <li
              key={index}
              className={`action-items-first-pull__step ${stateClass}`}
            >
              <span className="action-items-first-pull__step-icon" aria-hidden="true">
                {isDone ? (
                  <CheckIcon />
                ) : isActive ? (
                  <span className="action-items-first-pull__mini-spin" />
                ) : (
                  <span className="action-items-first-pull__dot" />
                )}
              </span>
              <span className="action-items-first-pull__step-text">{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ActionItemsFirstPullScreen;
